import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { API_BASE } from '../AuthContext';

interface GuideMeta { slug: string; title: string; order: number; }

export default function DocsPage() {
  const { slug } = useParams<{ slug?: string }>();
  const [guides, setGuides] = useState<GuideMeta[]>([]);
  const [markdown, setMarkdown] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/docs/guides`);
        const data = await res.json();
        setGuides(data.docs || []);
      } catch { setError('Failed to load documentation index.'); }
    })();
  }, []);

  const active = slug || guides[0]?.slug;

  useEffect(() => {
    if (!active) return;
    setMarkdown('');
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/docs/guides/${active}`);
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        setMarkdown(data.markdown || '');
      } catch { setError('Failed to load this guide.'); }
    })();
  }, [active]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="docs-page">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/70 border-b border-border/40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-14">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block w-6 h-6 rounded-md bg-primary" />
            <span className="font-semibold tracking-tight">CloudGenie · Docs</span>
          </Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">Sign in →</Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10 flex gap-10">
        <aside className="w-56 shrink-0 hidden md:block" data-testid="docs-sidebar">
          <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Guides</p>
          <nav className="space-y-1">
            {guides.map((g) => (
              <Link key={g.slug} to={`/docs/${g.slug}`} data-testid={`docs-link-${g.slug}`}
                    className={`block rounded-md px-3 py-1.5 text-sm transition-colors ${g.slug === active ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}>
                {g.title}
              </Link>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1" data-testid="docs-content">
          {error && <p role="alert" className="text-red-400" data-testid="docs-error">{error}</p>}
          {!error && !markdown && <p className="text-muted-foreground py-12" data-testid="docs-loading">Loading…</p>}
          {markdown && (
            <article className="docs-prose">
              <ReactMarkdown>{markdown}</ReactMarkdown>
            </article>
          )}
        </main>
      </div>
    </div>
  );
}
